// Bankkártya-űrlap tiszta segédei (nem-komponens): a form-értékek típusa, a betöltött sor → form-értékek
// leképezés, a form → wire-body építő, a resolver-séma (kliens-oldali elő-ellenőrzés) és a lokalizált zod-
// hibatérkép. Külön fájl a komponenstől, hogy a react-refresh (Fast Refresh) tiszta maradjon. A minta a
// features/billing-profiles/form.ts.
//
// A lejárat (hónap/év) a UI-ban STRING (input); a wire number-re alakítja (a contract coerce-e is ezt teszi).
import {
  type PaymentCardBrandSchema,
  type PaymentCardCreateBodySchema,
  type PaymentCardRowSchema,
  type PaymentCardWriteBodySchema,
} from '@atlas/contracts';
import { type TFunction } from 'i18next';
import { type z } from 'zod';
import { z as zod } from 'zod';

type PaymentCardRow = z.infer<typeof PaymentCardRowSchema>;
type WriteBody = z.input<typeof PaymentCardWriteBodySchema>;
type CreateBody = z.input<typeof PaymentCardCreateBodySchema>;
type Brand = z.infer<typeof PaymentCardBrandSchema>;

/** A kártyatípusok (a contract enum tükre — a select opció-forrása). */
export const CARD_BRANDS: Brand[] = ['visa', 'mastercard', 'amex', 'maestro', 'other'];

/** A bankkártya-form (mind string-alapú; a lejárat hónap/év STRING, a wire number-re parse-olja). */
export interface PaymentCardFormValues {
  /** A cél-ügyfél (create-kor választva; edit-kor zárolt — a sorból). */
  clientId: string;
  holderName: string;
  brand: string;
  last4: string;
  expiryMonth: string;
  expiryYear: string;
  bankName: string;
  note: string;
}

/** Üres form (create-mód default): visa kártyatípus, üres mezők. */
export const EMPTY_PAYMENT_CARD_FORM: PaymentCardFormValues = {
  clientId: '',
  holderName: '',
  brand: 'visa',
  last4: '',
  expiryMonth: '',
  expiryYear: '',
  bankName: '',
  note: '',
};

/** A betöltött kártya-sor → form-értékek (edit-mód). */
export function paymentCardToFormValues(row: PaymentCardRow): PaymentCardFormValues {
  return {
    clientId: row.clientId,
    holderName: row.holderName ?? '',
    brand: row.brand ?? 'visa',
    last4: row.last4 ?? '',
    expiryMonth: row.expiryMonth === null ? '' : String(row.expiryMonth),
    expiryYear: row.expiryYear === null ? '' : String(row.expiryYear),
    bankName: row.bankName ?? '',
    note: row.note ?? '',
  };
}

const nullable = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** A közös write-body (create törzse + update). A kliens a kártya-mezőket küldi; a `clientId`-t a busz injektálja. */
export function paymentCardFormToWriteBody(values: PaymentCardFormValues): WriteBody {
  return {
    holderName: values.holderName.trim(),
    brand: values.brand as Brand,
    last4: values.last4.trim(),
    expiryMonth: Number(values.expiryMonth),
    expiryYear: Number(values.expiryYear),
    bankName: nullable(values.bankName),
    note: nullable(values.note),
  };
}

/** Create-body: a write-törzs + a cél-ügyfél (CSAK create-nél). */
export function paymentCardFormToCreateBody(values: PaymentCardFormValues): CreateBody {
  return { ...paymentCardFormToWriteBody(values), clientId: values.clientId };
}

// ── Resolver-séma (kliens-oldali elő-ellenőrzés; a szerver a végső fal) ──

/**
 * A form-input validálása. A hiba-KÓDOK (message string) a `buildPaymentCardErrorMap`-ben fordulnak a
 * `validation:paymentCards.*` rétegre. A `clientId` csak create-kor kötelező — ezt a séma nem tudja (nincs mód-
 * flag a form-ban), ezért a clientId-t a komponens create-módban külön ellenőrzi (setError).
 */
// FONTOS: a séma NEM ad át üzenet-stringet a check-eknek (a lokalizáló hibatérkép a path+code alapján fordít —
// a billing-profiles/form.ts mintája).
export const paymentCardFormSchema = zod.object({
  clientId: zod.string(),
  holderName: zod.string().trim().min(1).max(255),
  brand: zod.enum(['visa', 'mastercard', 'amex', 'maestro', 'other']),
  last4: zod.string().regex(/^\d{4}$/),
  expiryMonth: zod.string().superRefine((raw, ctx) => {
    const n = Number(raw);
    if (raw.trim() === '' || !Number.isInteger(n) || n < 1 || n > 12) {
      ctx.addIssue({ code: zod.ZodIssueCode.custom });
    }
  }),
  expiryYear: zod.string().superRefine((raw, ctx) => {
    const n = Number(raw);
    if (raw.trim() === '' || !Number.isInteger(n) || n < 2000 || n > 2100) {
      ctx.addIssue({ code: zod.ZodIssueCode.custom });
    }
  }),
  bankName: zod.string().max(255),
  note: zod.string().max(2000),
});

/**
 * Lokalizált zod-hibatérkép: a MEZŐ (`issue.path[0]`) + a hiba-KÓD alapján a `validation:paymentCards.*` rétegre
 * fordít (a billing-profiles mintája). A séma NEM ad üzenetet, így ez a térkép fut le minden issue-ra.
 */
export function buildPaymentCardErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = typeof issue.path[0] === 'string' ? issue.path[0] : '';
    const msg = (k: string): { message: string } => ({
      message: t(`validation:paymentCards.${k}`),
    });
    const tooLong = issue.code === zod.ZodIssueCode.too_big;
    switch (field) {
      case 'holderName':
        return tooLong ? msg('tooLong') : msg('holderName');
      case 'brand':
        return msg('brand');
      case 'last4':
        return msg('last4');
      case 'expiryMonth':
        return msg('expiryMonth');
      case 'expiryYear':
        return msg('expiryYear');
      case 'bankName':
      case 'note':
        return msg('tooLong');
    }
    return { message: ctx.defaultError };
  };
}
